import { useApi, type ApiState } from "./useApi.js";

/**
 * E8.14 (NX-2643) — the injected roadmap-service hook. `Roadmap.tsx` and its sub-components read
 * the roadmap ONLY through this hook (AC1/AC5) — no sub-component performs its own `fetch`.
 *
 * Types here are a browser-safe MIRROR of `apps/api/src/roadmap.ts` (`RoadmapEntryView`), the same
 * pattern `roles.ts` and `routes/projectMetrics.ts` already use for the same reason: `@senesight/web`
 * never imports `@senesight/core` (Node-only: crypto, pg) or the API service. `DeliveryMode` /
 * `ConfidenceBasis` are copied the same way `Project.tsx` already copies them — see that file's
 * identical local re-declaration.
 *
 * A REAL, REPORTED GAP: the plan (E8.14) asks for a horizon selector offering "rest-of-year / next
 * 4·3·2·1 quarters". The shipped `/roadmap` (E7.10, NX-2642) supports exactly two horizons —
 * `ROADMAP_HORIZONS` in `apps/api/src/roadmap.ts` is `["next-quarter", "rest-of-year"]`. There is no
 * 4/3/2-quarter option server-side, and this hook deliberately does NOT fake one by fetching
 * `rest-of-year` and filtering it client-side to a narrower window — that would silently disagree
 * with the server's own `undated` count (computed against the SERVER's horizon boundary) and its
 * `MAX_SCAN` truncation, producing numbers that look plausible but are not what the server actually
 * scanned. `ROADMAP_HORIZONS` below mirrors only the two horizons that are real.
 */
export const ROADMAP_HORIZONS = ["next-quarter", "rest-of-year"] as const;
export type RoadmapHorizon = (typeof ROADMAP_HORIZONS)[number];

export type DeliveryMode = "scope-driven" | "date-driven" | "continuous";
export type ConfidenceBasis = "estimation" | "throughput" | "hybrid";

export interface RoadmapForecastValue {
  readonly modelVersion: string;
  readonly remainingItems: number;
  readonly p50Date: string;
  readonly p85Date: string;
  readonly p95Date: string;
}

export type RoadmapMetric<T> =
  | { readonly kind: "n/a"; readonly reason: string }
  | {
      readonly kind: "value";
      readonly value: T;
      readonly confidence:
        | { readonly kind: "n/a" }
        | { readonly kind: "value"; readonly confidence: number };
      readonly lineage: readonly number[];
    };

export interface RoadmapEntry {
  readonly id: string;
  readonly name: string;
  readonly scopeType: string;
  readonly parentProjectId: string | null;
  readonly forecast: RoadmapMetric<RoadmapForecastValue>;
  readonly targetDate: string | null;
  readonly deliveryMode: DeliveryMode | null;
  readonly confidenceBasis: ConfidenceBasis | null;
}

export interface RoadmapData {
  readonly entries: readonly RoadmapEntry[];
  /** Projects the horizon excluded for having neither forecast nor declared date. */
  readonly undated: number;
  /** True when the server's MAX_SCAN bounded the read: this page is a prefix, not the whole tenant. */
  readonly truncated: boolean;
}

const MAX_PAGE_LIMIT = 100;

/**
 * Fetch the roadmap for one horizon. A single page at the server's max page size (100) — full
 * cursor pagination beyond that first page is out of scope for this story (reported as a gap, not
 * silently dropped: see the final report). `envelope.meta.nextCursor` still comes through, so a
 * caller that wants to detect "there is more" can (Roadmap.tsx does, alongside `truncated`).
 */
export function useRoadmap(horizon: RoadmapHorizon): ApiState<RoadmapData> {
  return useApi<RoadmapData>(`/roadmap?horizon=${horizon}&limit=${MAX_PAGE_LIMIT}`);
}
